/**
 * 파일명 : evl0106m.tsx
 * 설명   : 프로젝트 평가 (팀원) - 모바일
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용
 ************************************************
 * 1.0        2025-12-10    김준우    최초작성
 * 1.1        2026-01-22    조동현    모바일 버전 생성
 ************************************************/

import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Select,
  Input,
  Button,
  Spin,
  message,
  Typography,
  Form,
  Card,
  Tooltip,
  Modal,
  Divider,
} from "antd";
import {
  CheckCircleFilled,
  SendOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import * as common from "libs/common";
import * as commonProxy from "libs/CommonProxy";
import { isMobile } from "react-device-detect";
import type {
  IPropsEvl0106M,
  CommonEvaluation,
  JobEvaluation,
} from "../../../redux/models/evl/evl0106m_model";

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function Evl0106m(props: IPropsEvl0106M) {
  const [form] = Form.useForm();

  // 화면 너비 상태
  const [screenWidth, setScreenWidth] = React.useState<number>(
    window.innerWidth,
  );

  // Props에서 받은 편집 가능 여부 ('Y': 수정 가능, 'N': 읽기 전용)
  const evlEnableYn = props.evlEnableYn ?? "Y";

  // 상태 관리
  const [loading, setLoading] = useState<boolean>(true);
  const [submitYn, setSubmitYn] = useState<string>("N"); // 제출 여부
  const [isWithinDeadline, setIsWithinDeadline] = useState<boolean>(false); // 마감일 이내 여부
  const [isPmMatched, setIsPmMatched] = useState<boolean>(false); // PM 권한 여부
  const [evlId, setEvlId] = useState<string>(""); // pjMapId 대신 evlId 사용
  const [projectCd, setProjectCd] = useState<string>(""); // 프로젝트 코드 추가
  const [urNm, setUrNm] = useState<string>("");
  const [dept, setDept] = useState<string>("");
  const [pm, setPm] = useState<string>("");
  const [pjtNm, setPjtNm] = useState<string>("");
  const [subSysNm, setSubSysNm] = useState<string>(""); // subSys → subSysNm
  const [jobRole, setJobRole] = useState<string>("팀원");
  const [pmEvlRmk, setPmEvlRmk] = useState<string>(""); // evlComment → pmEvlRmk
  const [commonEvalData, setCommonEvalData] = useState<CommonEvaluation[]>([]);
  const [jobEvalData, setJobEvalData] = useState<JobEvaluation[]>([]);
  const confirmModalRefs = useRef<any[]>([]);
  const subSysRef = useRef<HTMLDivElement | null>(null);
  const commonTableRef = useRef<HTMLDivElement | null>(null);
  const jobTableRef = useRef<HTMLDivElement | null>(null);
  const commentsRef = useRef<HTMLDivElement | null>(null);

  const removeConfirmModalRef = (targetModal: any) => {
    if (!targetModal) return;
    confirmModalRefs.current = confirmModalRefs.current.filter(
      (modalRef) => modalRef !== targetModal,
    );
  };

  const closeOpenConfirmModals = () => {
    confirmModalRefs.current.forEach((modalRef) => {
      try {
        modalRef?.destroy?.();
      } catch (error) {
        // ignore
      }
    });
    confirmModalRefs.current = [];
  };

  const openConfirmModal = (config: any) => {
    let createdModal: any;
    createdModal = Modal.confirm({
      ...config,
      onOk: (...args: any[]) => {
        const result = config?.onOk?.(...args);
        removeConfirmModalRef(createdModal);
        return result;
      },
      onCancel: (...args: any[]) => {
        const result = config?.onCancel?.(...args);
        removeConfirmModalRef(createdModal);
        return result;
      },
    });

    confirmModalRefs.current.push(createdModal);
    return createdModal;
  };

  const scrollAndError = (
    ref: React.RefObject<HTMLElement>,
    errorMsg: string,
  ) => {
    message.error(errorMsg);
    setTimeout(() => {
      if (ref.current) {
        ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
        ref.current.focus?.();
      }
    }, 300);
  };

  const scrollToRowSelector = (selector: string) => {
    setTimeout(() => {
      const element = document.querySelector(selector);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 300);
  };

  // 선택한 직무에 따라 필터링된 평가 항목
  const filteredJobEvalData = jobEvalData.filter(
    (item) => item.jobType === jobRole,
  );

  // 입력 필드 비활성화 조건: evlEnableYn='N' 또는 submitYn='Y'
  const isDisabled = evlEnableYn === "N" || submitYn === "Y";
  const hasActionButtons =
    isPmMatched &&
    ((submitYn !== "Y" && evlEnableYn === "Y") ||
      (submitYn === "Y" && isWithinDeadline));

  // 데이터 로드 함수
  const loadData = () => {
    setLoading(true);

    // Props에서 받은 데이터로 요청
    const requestData = {
      evlId: props.evlId,
      empNo: props.empNo || common.getEmpNo(),
      deptCd: common.getDeptCd(),
    };

    // 디버깅: 요청 데이터 확인

    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLR0004",
      success: (responseData: any) => {
        const data = responseData.data || responseData;
        const memberData = data.EvlPjtPmItems || {};

        // PM 권한 확인
        const pjPmEmpNo = (props.pjPmEmpNo || "").toString().trim();
        const currentEmpNo = (common.getEmpNo() || "").toString().trim();
        setIsPmMatched(pjPmEmpNo !== "" && pjPmEmpNo === currentEmpNo);

        // 제출 여부 설정 (대소문자 구분 없이 처리)
        const submitYnValue = (responseData.submitYn || "N")
          .toString()
          .toUpperCase();
        setSubmitYn(submitYnValue);

        // 마감일 이내 여부 설정 (서버에서 계산한 값 사용)
        const isWithinDeadlineValue =
          responseData?.isWithinDeadline === true ||
          responseData?.isWithinDeadline === "Y";
        setIsWithinDeadline(isWithinDeadlineValue);

        // 서버 응답 데이터의 모든 필드 확인

        if (memberData.urNm) {
          setEvlId(memberData.evlId || props.evlId || "");
          // 여러 가능한 필드명 시도
          setProjectCd(
            memberData.projectCd ||
              memberData.pjtCd ||
              memberData.prjCd ||
              memberData.projCd ||
              memberData.pjCd ||
              "",
          );
          setUrNm(memberData.urNm || "");
          setDept(memberData.deptNm || "");
          setPm(memberData.pmNm || memberData.pm || ""); // pmNm 우선 사용
          setPjtNm(memberData.pjtNm || props.pjNm || ""); // Props 파라미터 우선
          setSubSysNm(memberData.subSysNm || memberData.subSys || ""); // subSysNm 우선, subSys 대체

          // jobCd를 jobRole로 변환
          const convertedJobRole = memberData.jobCd
            ? getJobNameFromCode(memberData.jobCd)
            : memberData.jobRole || "팀원";
          setJobRole(convertedJobRole);

          setPmEvlRmk(memberData.pmEvlRmk || memberData.evlComment || ""); // pmEvlRmk 우선, evlComment 대체
        }

        const evlItems = memberData.evlItems || [];

        if (evlItems.length === 0) {
          setCommonEvalData([]);
          setJobEvalData([]);
          setLoading(false);
          return;
        }

        // dtlTypCd별로 그룹화
        const groupedByDtlTypCd: Record<string, any[]> = {};
        evlItems.forEach((item: any, idx: number) => {
          // hrEvlItmId의 5~6번째 문자열이 dtlTypCd
          const hrEvlItmId =
            item.evlItemId || item.hrEvlItmId || item.evlItmId || "";
          const dtlTypCd =
            hrEvlItmId.length >= 6 ? hrEvlItmId.substring(4, 6) : "unknown";

          if (!groupedByDtlTypCd[dtlTypCd]) {
            groupedByDtlTypCd[dtlTypCd] = [];
          }
          groupedByDtlTypCd[dtlTypCd].push(item);
        });

        // 공통 평가 항목 (dtlTypCd === '17')
        const commonItems: CommonEvaluation[] = [];
        if (groupedByDtlTypCd["17"]) {
          groupedByDtlTypCd["17"].forEach((item: any, index: number) => {
            const commonItem = {
              key: `c${index + 1}`,
              hrEvlItmId: item.evlItemId || item.hrEvlItmId || item.evlItmId,
              category: item.itmNm || "공통",
              item: `${index + 1}. ${item.desc1 || item.itmDesc || ""}`,
              score: Number(item.baseAsgn || 0),
              pmGrade: item.pmEvlScr || item.pmGrade || null,
            };
            commonItems.push(commonItem);
          });
        }
        setCommonEvalData(commonItems);

        // 직무별 평가 항목 (dtlTypCd !== '17')
        const jobItems: JobEvaluation[] = [];
        const jobDtlTypCds = Object.keys(groupedByDtlTypCd).filter(
          (dtlTypCd) => dtlTypCd !== "17",
        );

        jobDtlTypCds.forEach((dtlTypCd) => {
          const items = groupedByDtlTypCd[dtlTypCd];
          const jobTypeName = items[0]?.itmNm || `직무${dtlTypCd}`;

          items.forEach((item: any, index: number) => {
            const jobItem = {
              key: `${dtlTypCd}-${index + 1}`,
              hrEvlItmId: item.evlItemId || item.hrEvlItmId || item.evlItmId,
              jobType: jobTypeName,
              item: `${index + 1}. ${item.desc1 || item.itmDesc || ""}`,
              score: Number(item.baseAsgn || 0),
              pmGrade: item.pmEvlScr || item.pmGrade || null,
            };
            jobItems.push(jobItem);
          });
        });

        setJobEvalData(jobItems);

        message.success("프로젝트 평가내용 조회완료");
        setLoading(false);
      },
      error: (errorMsg: string) => {
        message.error(errorMsg || "데이터를 불러오는 중 오류가 발생했습니다.");
        setLoading(false);
      },
    });
  };

  // 데이터 로드
  useEffect(() => {
    // props.evlId 체크
    if (!props.evlId) {
      // loading을 true로 유지하여 background에 로딩 표시
      setLoading(true);
      openConfirmModal({
        title: "인사평가 메인페이지에서 접근하십시오.",
        okText: "확인",
        cancelButtonProps: { style: { display: "none" } }, // 취소 버튼 숨김
        maskClosable: false, // 바깥 클릭 방지
        keyboard: false, // ESC 방지
        onOk: () => {
          // 인사평가 메인 페이지로 이동
          common.openPage({ menuId: "MEVL2025002" });
        },
        onCancel() {
          // 인사평가 메인 페이지로 이동
          common.openPage({ menuId: "MEVL2025002" });
        },
      });
      return; // 이후 코드 실행 안 함
    }
    loadData();
  }, []);

  /**
   * 화면 크기 감지
   * 설명 : 화면 크기 변경시 상태 업데이트
   */
  React.useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      closeOpenConfirmModals();
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      closeOpenConfirmModals();
    };
  }, []);

  // 공통 평가 테이블 컬럼
  const commonEvalColumns = [
    {
      title: <div style={{ textAlign: "center" }}>평가항목</div>,
      dataIndex: "item",
      key: "item",
      render: (text: string, record: any) => (
        <div
          style={{
            fontWeight: "normal",
            textAlign: "left",
          }}
        >
          {text}
        </div>
      ),
    },
    {
      title: "배점",
      dataIndex: "score",
      key: "score",
      width: 60,
      align: "center" as const,
    },
    {
      title: "PM",
      dataIndex: "pmGrade",
      key: "pmGrade",
      width: 80,
      align: "center" as const,
      render: (value: string | null, record: any) => {
        return (
          <Select
            value={value}
            onChange={(grade) => {
              const newData = commonEvalData.map((item) =>
                item.key === record.key ? { ...item, pmGrade: grade } : item,
              );
              setCommonEvalData(newData);
            }}
            style={{ width: "100%" }}
            size="small"
            placeholder="선택"
            disabled={isDisabled}
          >
            <Select.Option value="A">
              <div style={{ textAlign: "center" }}>A</div>
            </Select.Option>
            <Select.Option value="B">
              <div style={{ textAlign: "center" }}>B</div>
            </Select.Option>
            <Select.Option value="C">
              <div style={{ textAlign: "center" }}>C</div>
            </Select.Option>
            <Select.Option value="D">
              <div style={{ textAlign: "center" }}>D</div>
            </Select.Option>
          </Select>
        );
      },
    },
  ];

  // 직무 평가 테이블 컬럼
  const jobEvalColumns = [
    {
      title: <div style={{ textAlign: "center" }}>평가항목</div>,
      dataIndex: "item",
      key: "item",
      render: (text: string, record: any) => (
        <div
          style={{
            fontWeight: "normal",
            textAlign: "left",
          }}
        >
          {text}
        </div>
      ),
    },
    {
      title: "배점",
      dataIndex: "score",
      key: "score",
      width: 60,
      align: "center" as const,
    },
    {
      title: "PM",
      dataIndex: "pmGrade",
      key: "pmGrade",
      width: 80,
      align: "center" as const,
      render: (value: string | null, record: any) => {
        return (
          <Select
            value={value}
            onChange={(grade) => {
              const newData = jobEvalData.map((item) =>
                item.key === record.key ? { ...item, pmGrade: grade } : item,
              );
              setJobEvalData(newData);
            }}
            style={{ width: "100%" }}
            size="small"
            placeholder="선택"
            disabled={isDisabled}
          >
            <Select.Option value="A">
              <div style={{ textAlign: "center" }}>A</div>
            </Select.Option>
            <Select.Option value="B">
              <div style={{ textAlign: "center" }}>B</div>
            </Select.Option>
            <Select.Option value="C">
              <div style={{ textAlign: "center" }}>C</div>
            </Select.Option>
            <Select.Option value="D">
              <div style={{ textAlign: "center" }}>D</div>
            </Select.Option>
          </Select>
        );
      },
    },
  ];

  // 점수 계산 함수
  const getGradeMultiplier = (grade: string | null): number => {
    switch (grade) {
      case "A":
        return 1.0;
      case "B":
        return 0.75;
      case "C":
        return 0.5;
      case "D":
        return 0.25;
      default:
        return 0;
    }
  };

  // 직무를 JOB_CD로 변환하는 함수
  const getJobCode = (jobRole: string): string => {
    switch (jobRole) {
      case "M/M":
        return "01";
      case "팀원":
        return "02";
      case "컨설턴트":
        return "03";
      default:
        return "02"; // 기본값 팀원
    }
  };

  // JOB_CD를 직무명으로 변환하는 함수
  const getJobNameFromCode = (jobCd: string): string => {
    switch (jobCd) {
      case "01":
        return "M/M";
      case "02":
        return "팀원";
      case "03":
        return "컨설턴트";
      default:
        return "팀원"; // 기본값 팀원
    }
  };

  const calculateCommonPmScore = (): number => {
    return commonEvalData
      .filter((item) => item.item !== "계" && item.item !== "소계")
      .reduce(
        (sum, item) => sum + item.score * getGradeMultiplier(item.pmGrade),
        0,
      );
  };

  const calculateJobPmScore = (jobType: string): number => {
    return jobEvalData
      .filter(
        (item) =>
          item.jobType === jobType &&
          item.item !== "계" &&
          item.item !== "소계",
      )
      .reduce(
        (sum, item) => sum + item.score * getGradeMultiplier(item.pmGrade),
        0,
      );
  };

  const calculateCommonMaxScore = (): number => {
    return commonEvalData
      .filter((item) => item.item !== "계" && item.item !== "소계")
      .reduce((sum, item) => sum + item.score, 0);
  };

  const calculateJobMaxScore = (jobType: string): number => {
    return jobEvalData
      .filter(
        (item) =>
          item.jobType === jobType &&
          item.item !== "계" &&
          item.item !== "소계",
      )
      .reduce((sum, item) => sum + item.score, 0);
  };

  // 저장 버튼 핸들러
  const handleSave = () => {
    openConfirmModal({
      title: "저장 확인",
      content: "저장하시겠습니까?",
      okText: "저장",
      cancelText: "취소",
      onOk: () => {
        performSave();
      },
      onCancel: () => {
        message.info({
          content: "저장 취소",
          duration: 2,
        });
      },
    });
  };

  const performSave = () => {
    // 직무 코드 변환
    const jobCode = getJobCode(jobRole);

    // 현재 선택된 직무에 해당하는 평가 항목만 필터링 (소계 제외)
    const filteredJobEvalItems = jobEvalData.filter(
      (item) => item.item !== "소계" && item.jobType === jobRole,
    );

    // 총점 계산
    const totalScore = calculateCommonPmScore() + calculateJobPmScore(jobRole);

    // 평가년도 추출 (evlId의 앞 4자리)
    const evlYear = evlId ? evlId.substring(0, 4) : "";

    const saveData = {
      evlId: evlId,
      evlYear: evlYear, // 평가년도 추가
      projectCd: projectCd, // 프로젝트 코드 추가
      empNo: common.getEmpNo(),
      urNm: urNm,
      dept: dept,
      pm: pm,
      pjtNm: pjtNm,
      subSysNm: subSysNm, // subSys → subSysNm
      evlCd: "04", // 평가코드
      jobCd: jobCode, // 직무 코드 추가
      jobRole: jobRole,
      pmEvlRmk: pmEvlRmk, // evlComment → pmEvlRmk
      pmEvlTotScr: totalScore, // totalScore → pmEvlTotScr
      commonEvalItems: commonEvalData.filter((item) => item.item !== "소계"),
      jobEvalItems: filteredJobEvalItems, // 선택된 직무만 전송
      submitYn: "N",
    };

    commonProxy.callAsyncSvc(saveData, {
      txCd: "TREVLS0005",
      success: (responseData: any) => {
        // 응답 데이터에서 resultCode와 resultMsg 추출
        const resultCode =
          responseData?.data?.resultCode || responseData?.resultCode;
        const resultMsg =
          responseData?.data?.resultMsg || responseData?.resultMsg;

        // resultCode에 따른 처리
        if (resultCode === "200" || resultCode === "0" || !resultCode) {
          // 성공
          message.success(resultMsg || "평가 내용이 저장되었습니다.");
          // 저장 후 데이터 재조회
          loadData();
        } else if (resultCode === "400") {
          message.error({
            content: resultMsg || "이미 기 제출된 평가입니다.",
            duration: 3,
          });
        } else {
          // 기타 에러 (500 등)
          message.error({
            content: resultMsg || "저장 중 오류가 발생했습니다.",
            duration: 3,
          });
        }
      },
      error: (errorMsg: string) => {
        message.error(errorMsg || "저장 중 오류가 발생했습니다.");
      },
    });
  };

  // 제출취소 버튼 핸들러
  const handleCancelSubmit = () => {
    openConfirmModal({
      title: "제출을 취소하시겠습니까?",
      content: "제출취소 후 기한 내에 다시 제출할 수 있습니다.",
      okText: "확인",
      cancelText: "취소",
      onOk: () => {
        performCancelSubmit();
      },
    });
  };

  // 실제 제출취소 로직 실행 (submitYn='N'으로 제출)
  const performCancelSubmit = () => {
    const evlYear = evlId ? evlId.substring(0, 4) : "";

    const requestData = {
      evlId: evlId,
      evlYear: evlYear,
      empNo: common.getEmpNo(),
      evlCd: "04",
      submitYn: "N",
    };

    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLS0006",
      success: (responseData: any) => {
        const resultCode =
          responseData?.data?.resultCode || responseData?.resultCode;
        const resultMsg =
          responseData?.data?.resultMsg || responseData?.resultMsg;

        if (resultCode === "400") {
          openConfirmModal({
            title: "제출취소 실패",
            content: resultMsg || "제출취소에 실패했습니다.",
            okText: "확인",
            cancelButtonProps: { style: { display: "none" } },
            onOk: () => {
              common.openPage({ menuId: "MEVL2025002" });
            },
          });
        } else {
          message.success("제출취소 되었습니다.");
          setTimeout(() => {
            common.openPage({ menuId: "MEVL2025002" });
          }, 800);
        }
      },
      error: (errorMsg: string) => {
        openConfirmModal({
          title: "오류",
          content: errorMsg || "제출취소에 실패했습니다.",
          okText: "확인",
          cancelButtonProps: { style: { display: "none" } },
          onOk: () => {
            common.openPage({ menuId: "MEVL2025002" });
          },
        });
      },
    });
  };

  // 제출 버튼 핸들러
  const handleSubmit = () => {
    // Sub System 필수 체크 추가
    if (!subSysNm || subSysNm.trim() === "") {
      scrollAndError(subSysRef, "Sub System을 입력해주세요.");
      return;
    }

    const unevaluatedCommon = commonEvalData.filter(
      (item) => item.item !== "계" && item.item !== "소계" && !item.pmGrade,
    );

    if (unevaluatedCommon.length > 0) {
      message.error({
        content: `공통 평가 항목 중 평가하지 않은 항목이 ${unevaluatedCommon.length}개 있습니다.`,
        duration: 3,
      });
      const firstCommon = unevaluatedCommon[0];
      if (firstCommon) {
        scrollToRowSelector(`[data-common-key="${firstCommon.key}"]`);
      } else if (commonTableRef.current) {
        scrollAndError(commonTableRef, "공통 평가 항목을 확인해주세요.");
      }
      return;
    }

    const unevaluatedJob = jobEvalData.filter(
      (item) =>
        item.jobType === jobRole &&
        item.item !== "계" &&
        item.item !== "소계" &&
        !item.pmGrade,
    );

    if (unevaluatedJob.length > 0) {
      message.error({
        content: `${jobRole} 평가 항목 중 평가하지 않은 항목이 ${unevaluatedJob.length}개 있습니다.`,
        duration: 3,
      });
      const firstJob = unevaluatedJob[0];
      if (firstJob) {
        scrollToRowSelector(`[data-job-key="${firstJob.key}"]`);
      } else if (jobTableRef.current) {
        scrollAndError(jobTableRef, `${jobRole} 평가 항목을 확인해주세요.`);
      }
      return;
    }

    if (!pmEvlRmk || pmEvlRmk.trim() === "") {
      scrollAndError(commentsRef, "기타 평가 의견을 입력해주세요.");
      return;
    }

    openConfirmModal({
      title: "제출 확인",
      content: "제출하시겠습니까?\n제출완료 시 수정이 불가합니다.",
      okText: "제출",
      cancelText: "취소",
      onOk: () => {
        performSubmit();
      },
      onCancel: () => {
        message.info({
          content: "제출 취소",
          duration: 2,
        });
      },
    });
  };

  const performSubmit = () => {
    // 직무 코드 변환
    const jobCode = getJobCode(jobRole);

    // 현재 선택된 직무에 해당하는 평가 항목 필터링
    const filteredJobEvalItems = jobEvalData.filter(
      (item) => item.item !== "소계" && item.jobType === jobRole,
    );

    // 총점 계산
    const totalScore = calculateCommonPmScore() + calculateJobPmScore(jobRole);

    // 평가년도 추출 (evlId의 앞 4자리)
    const evlYear = evlId ? evlId.substring(0, 4) : "";

    const submitData = {
      evlId: evlId,
      evlYear: evlYear, // 평가년도 추가
      projectCd: projectCd, // 프로젝트 코드 추가
      empNo: common.getEmpNo(),
      urNm: urNm,
      dept: dept,
      pm: pm,
      pjtNm: pjtNm,
      subSysNm: subSysNm, // subSys → subSysNm
      evlCd: "04", // 평가코드
      jobCd: jobCode, // 직무 코드 추가
      jobRole: jobRole,
      pmEvlRmk: pmEvlRmk, // evlComment → pmEvlRmk
      pmEvlTotScr: totalScore, // totalScore → pmEvlTotScr
      commonEvalItems: commonEvalData.filter((item) => item.item !== "소계"),
      jobEvalItems: filteredJobEvalItems, // 선택된 직무만 전송
      submitYn: "Y",
    };

    commonProxy.callAsyncSvc(submitData, {
      txCd: "TREVLS0006",
      success: (responseData: any) => {
        // 응답 데이터에서 resultCode와 resultMsg 추출
        const resultCode =
          responseData?.data?.resultCode || responseData?.resultCode;
        const resultMsg =
          responseData?.data?.resultMsg || responseData?.resultMsg;

        // resultCode에 따른 처리
        if (resultCode === "200" || resultCode === "0" || !resultCode) {
          // 성공
          message.success(resultMsg || "제출되었습니다.");
          setTimeout(() => {
            common.openPage({ menuId: "MEVL2025002" });
          }, 800);
        } else if (resultCode === "400") {
          // 이미 제출된 평가 등의 에러
          openConfirmModal({
            title: "제출 실패",
            content: resultMsg || "이미 기 제출된 평가입니다.",
            okText: "확인",
            cancelButtonProps: { style: { display: "none" } },
            onOk: () => {
              common.openPage({ menuId: "MEVL2025002" });
            },
          });
        } else {
          // 기타 에러 (500 등)
          openConfirmModal({
            title: "제출 실패",
            content: resultMsg || "제출 중 오류가 발생했습니다.",
            okText: "확인",
            cancelButtonProps: { style: { display: "none" } },
            onOk: () => {
              common.openPage({ menuId: "MEVL2025002" });
            },
          });
        }
      },
      error: (errorMsg: string) => {
        openConfirmModal({
          title: "오류",
          content: errorMsg || "제출에 실패했습니다.",
          okText: "확인",
          cancelButtonProps: { style: { display: "none" } },
          onOk: () => {
            common.openPage({ menuId: "MEVL2025002" });
          },
        });
      },
    });
  };

  /**
   * comments 입력 변경 핸들러
   * 2000자 초과 시 Modal.confirm으로 경고하고 2000자로 제한
   */
  const handleCommentsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length > 2000) {
      setPmEvlRmk(value.slice(0, 2000));
      openConfirmModal({
        title: "※ 2000자를 초과하였습니다.",
        okText: "확인",
      });
    } else {
      setPmEvlRmk(value);
    }
  };

  // 로딩 화면
  if (loading)
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 999, // 모달(z-index: 1000)보다 낮게 설정하여 background에만 표시
          background: "rgba(255,255,255,0.6)",
        }}
      >
        <Spin size="large" />
      </div>
    );

  return (
    <>
      <div
        style={{
          padding: isMobile ? "12px" : "20px",
          marginTop: "30px",
          backgroundColor: "#f5f5f5",
          width: screenWidth - 20 + "px",
          paddingBottom: hasActionButtons ? "50px" : "10px",
        }}
      >
        <Title
          level={2}
          style={{
            textAlign: "center",
            marginBottom: "24px",
          }}
        >
          프로젝트 평가 (팀원)
        </Title>

        <Form form={form} layout="vertical">
          {/* 기본 정보 */}
          <Card
            title={
              <span style={{ fontSize: "16px", fontWeight: 600 }}>
                기본 정보
              </span>
            }
            style={{
              marginBottom: "15px",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
            styles={{ body: { padding: "20px" } }}
          >
            {/* 이름, 소속, PM - 3컬럼 그리드 */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "16px",
                marginBottom: "16px",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    marginBottom: "6px",
                    fontWeight: 500,
                  }}
                >
                  이름
                </div>
                <Tooltip title={urNm}>
                  <div
                    style={{
                      padding: "6px 10px",
                      backgroundColor: "#f5f5f5",
                      fontWeight: 500,
                      fontSize: "11px",
                      border: "1px solid #e8e8e8",
                      borderRadius: "6px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      textAlign: "center",
                    }}
                  >
                    {urNm}
                  </div>
                </Tooltip>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    marginBottom: "6px",
                    fontWeight: 500,
                  }}
                >
                  소속
                </div>
                <Tooltip title={dept}>
                  <div
                    style={{
                      padding: "6px 10px",
                      backgroundColor: "#f5f5f5",
                      fontWeight: 500,
                      fontSize: "11px",
                      border: "1px solid #e8e8e8",
                      borderRadius: "6px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      textAlign: "center",
                    }}
                  >
                    {dept}
                  </div>
                </Tooltip>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    marginBottom: "6px",
                    fontWeight: 500,
                  }}
                >
                  PM
                </div>
                <Tooltip title={pm}>
                  <div
                    style={{
                      padding: "6px 10px",
                      backgroundColor: "#f5f5f5",
                      fontWeight: 500,
                      fontSize: "11px",
                      border: "1px solid #e8e8e8",
                      borderRadius: "6px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      textAlign: "center",
                    }}
                  >
                    {pm}
                  </div>
                </Tooltip>
              </div>
            </div>

            {/* 프로젝트명 - 전체 너비 */}
            <div style={{ marginBottom: "16px" }}>
              <div
                style={{
                  fontSize: "12px",
                  marginBottom: "6px",
                  fontWeight: 500,
                }}
              >
                프로젝트명
              </div>
              <Tooltip title={pjtNm} overlayInnerStyle={{ fontSize: "13px" }}>
                <div
                  style={{
                    padding: "6px 10px",
                    backgroundColor: "#f5f5f5",
                    fontWeight: 500,
                    fontSize: "11px",
                    border: "1px solid #e8e8e8",
                    borderRadius: "6px",
                    width: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                  }}
                >
                  {pjtNm}
                </div>
              </Tooltip>
            </div>

            {/* Sub System & 직무 - 2컬럼 그리드 */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
              }}
            >
              <div ref={subSysRef}>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#ff4d4f",
                    marginBottom: "6px",
                    fontWeight: 600,
                  }}
                >
                  * Sub System
                </div>
                <Input
                  value={subSysNm}
                  onChange={(e) => setSubSysNm(e.target.value)}
                  placeholder="입력하세요"
                  disabled={isDisabled}
                  maxLength={100}
                  style={{
                    fontWeight: 500,
                    border: "1px solid #d9d9d9",
                    ...(isDisabled && {
                      backgroundColor: "#f5f5f5",
                      color: "rgba(0, 0, 0, 0.65)",
                    }),
                  }}
                />
              </div>
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#ff4d4f",
                    marginBottom: "6px",
                    fontWeight: 600,
                  }}
                >
                  * 직무
                </div>
                <Select
                  value={jobRole}
                  onChange={(value) => setJobRole(value)}
                  disabled={isDisabled}
                  style={{ width: "100%", fontWeight: 500 }}
                  size="middle"
                  options={[
                    {
                      value: "팀원",
                      label: (
                        <div style={{ textAlign: "center", fontWeight: 500 }}>
                          팀원
                        </div>
                      ),
                    },
                    {
                      value: "M/M",
                      label: (
                        <div style={{ textAlign: "center", fontWeight: 500 }}>
                          M/M
                        </div>
                      ),
                    },
                    {
                      value: "컨설턴트",
                      label: (
                        <div style={{ textAlign: "center", fontWeight: 500 }}>
                          컨설턴트
                        </div>
                      ),
                    },
                  ]}
                />
              </div>
            </div>
          </Card>

          {/* 평가 항목 (공통 + 직무별) */}
          <Card
            style={{
              marginBottom: "12px",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
            bodyStyle={{ padding: "16px" }}
          >
            <div style={{ marginBottom: "16px" }}>
              <Text strong style={{ fontSize: "16px", color: "#333" }}>
                평가 항목
              </Text>
            </div>

            <div
              style={{
                padding: "12px",
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
                fontSize: "10px",
                color: "#666",
              }}
            >
              평가방법: A (우수), B (양호), C (보통), D (미흡)의 4단계로 평가
            </div>

            <Divider
              style={{
                margin: "16px 0 8px 0",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              공통 평가 부분
            </Divider>

            <div ref={commonTableRef}>
              <Table
                key={`common-table-${commonEvalData.length}`}
                columns={commonEvalColumns}
                dataSource={commonEvalData}
                pagination={false}
                bordered
                size="small"
                onRow={(record) =>
                  ({
                    "data-common-key": record.key,
                  }) as React.HTMLAttributes<HTMLElement>
                }
              />
            </div>

            <Divider
              style={{
                margin: "16px 0 8px 0",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              {jobRole} 평가 부분
            </Divider>

            <div ref={jobTableRef}>
              <Table
                columns={jobEvalColumns}
                key={`job-table-${jobRole}`}
                dataSource={filteredJobEvalData}
                pagination={false}
                bordered
                size="small"
                onRow={(record) =>
                  ({
                    "data-job-key": record.key,
                  }) as React.HTMLAttributes<HTMLElement>
                }
              />
            </div>
          </Card>

          {/* 점수 요약 */}
          <Card
            style={{
              marginBottom: "15px",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
            bodyStyle={{ padding: "16px" }}
          >
            <div style={{ marginBottom: "16px" }}>
              <Text strong style={{ fontSize: "16px", color: "#333" }}>
                평가 결과
              </Text>
            </div>
            <Table
              key={`job-summary-${jobRole}`}
              dataSource={[
                {
                  key: "1",
                  category: "공통",
                  maxScore: calculateCommonMaxScore().toFixed(2),
                  score: calculateCommonPmScore().toFixed(2),
                },
                {
                  key: "2",
                  category: jobRole,
                  maxScore: calculateJobMaxScore(jobRole).toFixed(2),
                  score: calculateJobPmScore(jobRole).toFixed(2),
                },
                {
                  key: "3",
                  category: "총계",
                  maxScore: (
                    calculateCommonMaxScore() + calculateJobMaxScore(jobRole)
                  ).toFixed(2),
                  score: (
                    calculateCommonPmScore() + calculateJobPmScore(jobRole)
                  ).toFixed(2),
                },
              ]}
              columns={[
                {
                  title: "구분",
                  dataIndex: "category",
                  key: "category",
                  align: "center" as const,
                  width: 80,
                },
                {
                  title: "배점",
                  dataIndex: "maxScore",
                  key: "maxScore",
                  align: "center" as const,
                  width: 80,
                },
                {
                  title: "점수",
                  dataIndex: "score",
                  key: "score",
                  align: "center" as const,
                  width: 80,
                  render: (text, record) => {
                    if (record.category === "총계") {
                      return (
                        <span style={{ color: "red", fontWeight: "bold" }}>
                          {text}
                        </span>
                      );
                    }
                    return text;
                  },
                },
              ]}
              pagination={false}
              bordered
              size="small"
            />
          </Card>

          <Card
            style={{
              marginBottom: "12px",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
            bodyStyle={{ padding: "16px" }}
          >
            <div style={{ marginBottom: "16px" }}>
              <Text strong style={{ fontSize: "16px", color: "#333" }}>
                기타 평가 의견
              </Text>
            </div>

            <Form.Item>
              <div ref={commentsRef}>
                <TextArea
                  value={pmEvlRmk}
                  onChange={handleCommentsChange}
                  placeholder="평가 의견을 입력하세요"
                  rows={6}
                  disabled={isDisabled}
                  style={{ borderRadius: "8px" }}
                  maxLength={500}
                  showCount
                />
              </div>
            </Form.Item>
          </Card>
        </Form>
      </div>

      {/* 저장 및 제출 버튼 - 바깥쪽 고정 배치 */}
      {hasActionButtons && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            padding: "12px",
            backgroundColor: "#fff",
            borderTop: "1px solid #f0f0f0",
            display: "flex",
            gap: "8px",
            zIndex: 1000,
          }}
        >
          {submitYn !== "Y" && evlEnableYn === "Y" && (
            <>
              <Button
                size="large"
                icon={<SaveOutlined />}
                onClick={handleSave}
                disabled={isDisabled}
                style={{
                  flex: 1,
                  height: "48px",
                  fontSize: "16px",
                  fontWeight: 600,
                }}
              >
                저장
              </Button>
              <Button
                type="primary"
                size="large"
                onClick={handleSubmit}
                disabled={isDisabled}
                icon={<SendOutlined />}
                style={{
                  flex: 1,
                  height: "48px",
                  fontSize: "16px",
                  fontWeight: 600,
                }}
              >
                제출
              </Button>
            </>
          )}

          {submitYn === "Y" && isWithinDeadline && (
            <Button
              danger
              size="large"
              onClick={handleCancelSubmit}
              icon={<CheckCircleFilled />}
              style={{
                flex: 1,
                height: "48px",
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              제출취소
            </Button>
          )}
        </div>
      )}
    </>
  );
}
